// src/business/pages/BusinessOrders.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, MessageSquare } from "lucide-react";
import Swal from "../../utils/apparifySwal";

import Panel from "../components/Panel";
import OrderFulfillmentActions from "../components/OrderFulfillmentActions";
import { businessSwalBase } from "../utils/businessSwal";
import {
  canBusinessEditOrderStatus,
  canBusinessEditPaymentStatus,
  getBusinessOrderLifecycleNotice,
  getBusinessPaymentPresentation,
  getBusinessPaymentSystemNote,
} from "../utils/businessOrderPayment";
import { businessApi, request } from "../../services/api";
import ReviewMediaGallery from "../../components/reviews/ReviewMediaGallery";
import { normalizeReturnRequestMedia } from "../../utils/returnRequestMedia";
import { normalizeReviewMedia } from "../../utils/reviewMedia";
import {
  getSellerFulfillmentCommand,
  getSellerFulfillmentDisplay,
} from "../utils/businessOrderFulfillment";
import BusinessOrdersIndex from './orders/BusinessOrdersIndex';
import ShopSelect from "../../components/ShopSelect";
import { PageHeader } from "../../components/ui";

const ORDER_STATUSES = [
  "Pending",
  "Processing",
  "Packed",
  "Ready for Dispatch",
  "Shipped",
  "Out for Delivery",
  "Delivered",
  "Failed Delivery",
  "Cancelled",
];

const PAYMENT_STATUSES = ["Pending", "Paid", "Failed"];

function getPaymentStatusOptions(currentStatus) {
  const normalizedCurrent = normalizePaymentStatusLabel(currentStatus);
  return PAYMENT_STATUSES.includes(normalizedCurrent)
    ? PAYMENT_STATUSES
    : [normalizedCurrent, ...PAYMENT_STATUSES];
}

function money(n) {
  const num = Number(n || 0);
  return `₱ ${num.toLocaleString("en-PH")}`;
}

function safeDateTime(d) {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  const date = dt.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "2-digit" });
  const time = dt.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });
  return `${date} • ${time}`;
}

function normalizeOrderStatusLabel(status) {
  const normalized = String(status || "").trim().toLowerCase();

  switch (normalized) {
    case "processing":
    case "preparing":
    case "to ship":
    case "to_ship":
      return "Processing";
    case "packed":
      return "Packed";
    case "ready for dispatch":
    case "ready_for_dispatch":
      return "Ready for Dispatch";
    case "shipped":
    case "to receive":
    case "to_receive":
      return "Shipped";
    case "out for delivery":
    case "out_for_delivery":
      return "Out for Delivery";
    case "delivered":
    case "completed":
      return "Delivered";
    case "failed":
    case "failed delivery":
    case "failed_delivery":
      return "Failed Delivery";
    case "cancelled":
    case "canceled":
      return "Cancelled";
    case "return / refund":
    case "return refund":
    case "return_refund":
    case "refunded":
      return "Return / Refund";
    case "paid":
      return "Pending";
    case "pending":
    default:
      return "Pending";
  }
}

function normalizePaymentStatusLabel(status) {
  const normalized = String(status || "").trim().toLowerCase();

  switch (normalized) {
    case "awaiting payment":
    case "awaiting_payment":
      return "Awaiting Payment";
    case "payment expired":
    case "payment_expired":
    case "expired":
      return "Payment Expired";
    case "cancelled":
    case "canceled":
      return "Cancelled";
    case "paid":
      return "Paid";
    case "failed":
      return "Failed";
    case "refunded":
      return "Refunded";
    case "unpaid":
    case "pending":
    default:
      return "Pending";
  }
}

function orderStatusChipClasses(status) {
  const base = "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide border";
  switch (normalizeOrderStatusLabel(status).toLowerCase()) {
    case "pending":
      return `${base} border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300`;
    case "processing":
      return `${base} border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-200`;
    case "packed":
      return `${base} border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200`;
    case "shipped":
      return `${base} border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-900/40 dark:bg-violet-950/40 dark:text-violet-200`;
    case "out for delivery":
      return `${base} border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-900/40 dark:bg-fuchsia-950/40 dark:text-fuchsia-200`;
    case "delivered":
      return `${base} border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200`;
    case "failed delivery":
      return `${base} border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200`;
    case "cancelled":
      return `${base} border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200`;
    case "return / refund":
      return `${base} border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200`;
    default:
      return `${base} border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-400`;
  }
}

function paymentStatusChipClasses(status) {
  const base = "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide border";
  switch (normalizePaymentStatusLabel(status).toLowerCase()) {
    case "awaiting payment":
      return `${base} border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200`;
    case "payment expired":
      return `${base} border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200`;
    case "cancelled":
      return `${base} border-zinc-300 bg-zinc-100 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900/60 dark:text-zinc-300`;
    case "paid":
      return `${base} border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-200`;
    case "failed":
      return `${base} border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200`;
    case "refunded":
      return `${base} border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200`;
    case "pending":
    default:
      return `${base} border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300`;
  }
}

const FIELD_CLASS_NAME = "rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 outline-none transition-[background-color,border-color,box-shadow] duration-150 hover:border-zinc-300 hover:bg-zinc-50/60 focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200 dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-100 dark:hover:border-zinc-700 dark:hover:bg-zinc-900/60 dark:focus:border-zinc-600 dark:focus:ring-zinc-800";
const SECONDARY_BUTTON_CLASS_NAME = "rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-900 transition-all duration-150 hover:border-zinc-300 hover:bg-zinc-50 hover:shadow-sm active:scale-[0.99] dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-100 dark:hover:border-zinc-700 dark:hover:bg-zinc-900/60";
const TABLE_ROW_CLASS_NAME = "cursor-pointer transition-colors duration-150 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 focus-visible:outline-none focus-visible:bg-zinc-50 dark:focus-visible:bg-zinc-900/50";


function formatAddress(value) {
  if (!value) return "";
  if (typeof value === "string") return value;

  const parts = [
    value.street_address,
    value.address_line1,
    value.address_line2,
    value.landmark,
    value.barangay,
    value.city_municipality,
    value.city,
    value.province,
    value.region,
    value.postal_code,
    value.full_address,
  ].filter(Boolean);

  return parts.join(", ");
}

function normalizeReturnRequest(returnRequest) {
  if (!returnRequest) return null;

  const evidence = normalizeReturnRequestMedia(
    returnRequest?.evidence || returnRequest?.media,
    returnRequest?.meta?.evidence || returnRequest?.meta?.media
  );

  return {
    id: returnRequest?.id ?? null,
    orderId: returnRequest?.orderId ?? returnRequest?.order_id ?? null,
    status: returnRequest?.status || returnRequest?.requestStatus || returnRequest?.request_status || null,
    reason: returnRequest?.reason || null,
    details: returnRequest?.details || null,
    refundAmount:
      returnRequest?.refundAmount != null
        ? Number(returnRequest.refundAmount)
        : returnRequest?.refund_amount != null
          ? Number(returnRequest.refund_amount)
          : null,
    refundDestination: returnRequest?.refundDestination || returnRequest?.refund_destination || null,
    walletTransactionId: returnRequest?.walletTransactionId || returnRequest?.wallet_transaction_id || null,
    evidence,
    media: evidence,
    requestedAt:
      returnRequest?.requestedAt ||
      returnRequest?.requested_at ||
      returnRequest?.createdAt ||
      returnRequest?.created_at ||
      null,
    reviewedAt: returnRequest?.reviewedAt || returnRequest?.reviewed_at || null,
    refundedAt: returnRequest?.refundedAt || returnRequest?.refunded_at || null,
    reviewedByUserId: returnRequest?.reviewedByUserId || returnRequest?.reviewed_by_user_id || null,
    resolutionNote: returnRequest?.resolutionNote || returnRequest?.resolution_note || null,
  };
}

function normalizeShopperReview(review) {
  if (!review) return null;

  return {
    id: review?.id ?? null,
    rating: Number(review?.rating || 0),
    title: review?.title || null,
    reviewText: review?.reviewText || review?.review_text || null,
    status: review?.status || review?.review_status || null,
    recommended: review?.recommended == null ? null : Boolean(review.recommended),
    photos: Array.isArray(review?.photos) ? review.photos : [],
    media: normalizeReviewMedia(review?.media, review?.photos),
    likeCount: Number(review?.likeCount || review?.like_count || 0),
    createdAt: review?.createdAt || review?.created_at || null,
  };
}

function getReturnRequestStatusLabel(status) {
  switch (String(status || "").trim().toUpperCase()) {
    case "REFUNDED":
      return "Refunded";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "REQUESTED":
    default:
      return "Requested";
  }
}

function returnRequestChipClasses(status) {
  const base = "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide border";

  switch (String(status || "").trim().toUpperCase()) {
    case "REFUNDED":
      return `${base} border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200`;
    case "APPROVED":
      return `${base} border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200`;
    case "REJECTED":
      return `${base} border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200`;
    case "REQUESTED":
    default:
      return `${base} border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-200`;
  }
}

function getNextReturnRequest(order, payload = {}) {
  const current = normalizeReturnRequest(order?.returnRequest || order?.return_request);
  if (!current) return null;

  const explicitStatus = payload?.returnRequestStatus || payload?.return_request_status || null;
  const nextPaymentStatus = normalizePaymentStatusLabel(payload?.paymentStatus || order?.paymentStatus);
  const nextOrderStatus = normalizeOrderStatusLabel(payload?.status || order?.status);

  let status = explicitStatus || current.status || "REQUESTED";

  if (!explicitStatus) {
    if (nextPaymentStatus === "Refunded") {
      status = "REFUNDED";
    } else if (nextOrderStatus === "Return / Refund" && String(status).trim().toUpperCase() === "REQUESTED") {
      status = "APPROVED";
    }
  }

  const normalizedStatus = String(status || "REQUESTED").trim().toUpperCase();
  const needsReviewedAt = normalizedStatus === "APPROVED" || normalizedStatus === "REFUNDED" || normalizedStatus === "REJECTED";

  return {
    ...current,
    status,
    reviewedAt: needsReviewedAt ? current.reviewedAt || new Date().toISOString() : current.reviewedAt,
  };
}

function normalizeOrderItem(item, index) {
  const shopperReview = normalizeShopperReview(item?.shopperReview || item?.shopper_review);

  return {
    ...item,
    id: item?.id ?? `item-${index}`,
    name: item?.name || item?.productName || item?.product_name || item?.title || "",
    productName: item?.productName || item?.product_name || item?.name || item?.title || "",
    variation: item?.variation || item?.variantTitle || item?.variant_title || "",
    sku: item?.sku || item?.variantSku || item?.variant_sku || "",
    quantity: item?.quantity ?? item?.qty ?? 1,
    price: item?.price ?? item?.unitPrice ?? null,
    imageUrl: item?.imageUrl || item?.image_url || null,
    shopperReview,
    shopper_review: shopperReview,
  };
}

function normalizeOrder(o) {
  const id = o?.id ?? o?._id ?? o?.orderId ?? "-";
  const createdAt = o?.createdAt ?? o?.created_at ?? o?.date ?? null;
  const total = o?.total ?? o?.grandTotal ?? o?.amount ?? 0;
  const sellerFulfillmentStatus = o?.sellerFulfillmentStatus || o?.seller_fulfillment_status || null;
  const shipmentStatus = String(o?.shipmentStatus || o?.shipment_status || "").toUpperCase();
  const fulfillmentDisplay = getSellerFulfillmentDisplay(sellerFulfillmentStatus);
  const status = ["", "PENDING", "READY_FOR_DISPATCH", "CANCELLED"].includes(shipmentStatus)
    && fulfillmentDisplay
    ? fulfillmentDisplay
    : normalizeOrderStatusLabel(o?.status ?? o?.orderStatus ?? "Pending");
  const paymentStatus = normalizePaymentStatusLabel(o?.paymentStatus ?? o?.payment_status ?? "Pending");

  const customer =
    o?.customer?.name ||
    o?.customerName ||
    o?.customer ||
    o?.user?.name ||
    o?.user?.email ||
    o?.email ||
    "-";

  const email = o?.customer?.email || o?.customerEmail || o?.email || o?.user?.email || "";
  const phone = o?.customer?.phone || o?.phone || "";

  const items = Array.isArray(o?.items)
    ? o.items
    : Array.isArray(o?.order_items)
      ? o.order_items
      : Array.isArray(o?.orderItems)
        ? o.orderItems
        : [];
  const returnRequest = normalizeReturnRequest(o?.returnRequest || o?.return_request);
  const paymentMethod = o?.paymentMethod || o?.payment_method || null;
  const checkoutPayment = o?.checkoutPayment || o?.checkout_payment || null;

  const normalizedOrder = {
    ...o,
    id,
    reference:
      o?.sharedOrderReference ||
      o?.shared_order_reference ||
      o?.parentOrderReference ||
      o?.parent_order_reference ||
      o?.orderReference ||
      o?.order_reference ||
      null,
    sellerOrderReference:
      o?.sellerOrderReference ||
      o?.seller_order_reference ||
      null,
    createdAt,
    total,
    status,
    customer,
    email,
    phone,
    items: items.map(normalizeOrderItem),
    returnRequest,
    return_request: returnRequest,
    shipmentStatus: o?.shipmentStatus || o?.shipment_status || null,
    orderStatus: o?.orderStatus || o?.order_status || null,
    sellerFulfillmentStatus,
    fulfillmentStatusVersion: Number(o?.fulfillmentStatusVersion || o?.fulfillment_status_version || 1),
    paymentStatus,
    paymentMethod,
    checkoutPayment,
    customerNote: o?.customerNote || o?.customer_note || null,
    customer_note: o?.customer_note || o?.customerNote || null,
    checkout_payment: checkoutPayment,
    paidAt: o?.paidAt || o?.paid_at || null,
    cancellationReason: o?.cancellationReason || o?.cancellation_reason || null,
    shippingAddress: formatAddress(o?.shippingAddress || o?.shipping_address || null),
    billingAddress: formatAddress(o?.billingAddress || o?.billing_address || null),
  };

  const paymentPresentation = getBusinessPaymentPresentation(normalizedOrder);

  return {
    ...normalizedOrder,
    checkoutPayment: paymentPresentation.checkoutPayment,
    checkout_payment: paymentPresentation.checkoutPayment,
    paymentStatusLabel: paymentPresentation.label,
    paymentStatusMessage: paymentPresentation.helperText,
    paymentSystemManaged: paymentPresentation.systemManaged,
    canEditPaymentStatus: canBusinessEditPaymentStatus(normalizedOrder),
    canEditOrderStatus: canBusinessEditOrderStatus(normalizedOrder),
    fulfillmentNotice: getBusinessOrderLifecycleNotice(normalizedOrder),
  };
}

function statusFromErrMessage(msg) {
  const m = String(msg || "");
  const match = m.match(/Request failed:\s*(\d{3})/);
  return match ? Number(match[1]) : null;
}

function getVisibleOrderId(order) {
  return order?.reference || "-";
}

function getShopperCancellationReason(order) {
  const rawReason = String(order?.cancellationReason || "").trim();
  if (!rawReason) return "";
  return rawReason.replace(/^shopper:\s*/i, "").trim();
}

function isShopperCancellationRequested(order) {
  if (normalizeOrderStatusLabel(order?.status) !== "Cancelled") return false;
  return Boolean(getShopperCancellationReason(order));
}

function LegacyBusinessOrders({ pageTitle, pageSubtitle, panelTitle = 'Orders', initialStatus = 'All', allowedStatuses = ORDER_STATUSES }) {
  const swalBase = useMemo(
    () => ({
      ...businessSwalBase,
      target: "#business-swal-root",
    }),
    []
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);

  const [query, setQuery] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  useEffect(() => { setStatusFilter(initialStatus); }, [initialStatus, pageTitle]);

  const [searchParams] = useSearchParams();
  const [openId, setOpenId] = useState(searchParams.get("orderId"));
  useEffect(() => { setOpenId(searchParams.get("orderId")); }, [searchParams]);
  const openOrder = useMemo(
    () => orders.find((o) => String(o.id) === String(openId)) || null,
    [orders, openId]
  );
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const [, setEvents] = useState([]);
  const [, setLoadingEvents] = useState(false);
  async function refreshOrderActivity(orderId) {
    if (!orderId) return;
    setLoadingEvents(true);
    try {
      const res = await businessApi.getOrderActivity(orderId);
      setEvents(res.data || res || []);
    } catch (err) {
      console.error("Failed to fetch events", err);
    } finally {
      setLoadingEvents(false);
    }
  }

  useEffect(() => {
    if (!openId) {
      setEvents([]);
      return;
    }

    let active = true;
    setLoadingEvents(true);

    businessApi.getOrderActivity(openId)
      .then((res) => {
        if (active) setEvents(res.data || res || []);
      })
      .catch((err) => {
        console.error("Failed to fetch events", err);
      })
      .finally(() => {
        if (active) setLoadingEvents(false);
      });

    return () => { active = false; };
  }, [openId]);

  function showToast(type, msg) {
    setToast({ type, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1600);
  }

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(query.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [query]);

  async function load({ withSwal = false } = {}) {
    setLoading(true);
    setError("");

    if (withSwal) {
      Swal.fire({
        ...swalBase,
        title: "Loading…",
        html: "Fetching orders",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
    }

    try {
      const data = await businessApi.getOrders();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.data?.items)
              ? data.data.items
              : [];

      setOrders(list.map(normalizeOrder));
      if (withSwal) Swal.close();
    } catch (e) {
      if (withSwal) Swal.close();

      const msg = e?.message || "Failed to load orders.";
      setError(msg);
      setOrders([]);

      const code = statusFromErrMessage(msg);
      if (code === 403) {
        await Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Access denied",
          html: "You don’t have permission to view orders.",
          confirmButtonText: "OK",
        });
      } else {
        await Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Failed to load",
          html: msg,
          confirmButtonText: "OK",
        });
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function changeOrderStatus(id, status) {
    const order = orders.find((candidate) => String(candidate.id) === String(id));
    const command = getSellerFulfillmentCommand(status);
    if (!order || !command) {
      await Swal.fire({ ...swalBase, icon: "error", title: "Invalid fulfillment action" });
      return;
    }
    const confirm = await Swal.fire({
      ...swalBase,
      icon: "question",
      title: "Update order status?",
      html: `Set order <b>${id}</b> fulfillment to <b>${status}</b>?`,
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    const prev = orders;
    setOrders((cur) =>
      cur.map((o) =>
        String(o.id) === String(id)
          ? (() => {
              const nextReturnRequest = getNextReturnRequest(o, { status });

              return {
                ...o,
                status: normalizeOrderStatusLabel(status),
                returnRequest: nextReturnRequest,
                return_request: nextReturnRequest,
              };
            })()
          : o
      )
    );
    showToast("info", "Updating order status…");

    try {
      const durableCommand = command === "ready-for-dispatch" || command === "cancel-before-pickup";
      const res = await request({
        url: `/api/business/delivery/orders/${id}/fulfillment/${command}`,
        method: "POST",
        data: { expectedVersion: order.fulfillmentStatusVersion },
        headers: {
          "X-Portal": "business",
          ...(durableCommand ? {
            "Idempotency-Key": window.crypto?.randomUUID?.()
              || `fulfillment-${id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          } : {}),
        },
      });
      const payload = res?.data || res || {};

      setOrders((cur) =>
        cur.map((o) =>
          String(o.id) === String(id)
            ? (() => {
                const nextReturnRequest = getNextReturnRequest(o, {
                  status: payload.compatibility?.status || status,
                  paymentStatus: payload.paymentStatus || o.paymentStatus,
                  returnRequestStatus: payload.returnRequestStatus || null,
                });

                return {
                  ...o,
                  status: normalizeOrderStatusLabel(payload.fulfillmentStatus || status),
                  storedStatus: payload.compatibility?.status || o.storedStatus || null,
                  orderStatus: payload.compatibility?.orderStatus || o.orderStatus,
                  sellerFulfillmentStatus: payload.fulfillmentStatus || o.sellerFulfillmentStatus,
                  fulfillmentStatusVersion: Number(payload.statusVersion || o.fulfillmentStatusVersion),
                  shipmentStatus: payload.shipmentStatus ?? o.shipmentStatus,
                  paymentStatus: normalizePaymentStatusLabel(payload.paymentStatus || o.paymentStatus),
                  returnRequest: nextReturnRequest,
                  return_request: nextReturnRequest,
                };
              })()
            : o
        )
      );

      if (String(openId) === String(id)) {
        await refreshOrderActivity(id);
      }

      showToast("success", "Order status updated");
    } catch (e) {
      setOrders(prev);
      const msg = e?.message || "Failed to update order status.";

      const code = statusFromErrMessage(msg);
      if (code === 403) {
        await Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Access denied",
          html: "You don’t have permission to update order status.",
          confirmButtonText: "OK",
        });
      } else {
        await Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Update failed",
          html: msg,
          confirmButtonText: "OK",
        });
      }
    }
  }

  async function changePaymentStatus(id, paymentStatus) {
    const confirm = await Swal.fire({
      ...swalBase,
      icon: "question",
      title: "Update payment status?",
      html: `Set order <b>${id}</b> payment to <b>${paymentStatus}</b>?`,
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    const prev = orders;
    setOrders((cur) =>
      cur.map((o) =>
        String(o.id) === String(id)
          ? (() => {
              const nextReturnRequest = getNextReturnRequest(o, { paymentStatus });

              return {
                ...o,
                paymentStatus: normalizePaymentStatusLabel(paymentStatus),
                returnRequest: nextReturnRequest,
                return_request: nextReturnRequest,
              };
            })()
          : o
      )
    );
    showToast("info", "Updating payment status…");

    try {
      const res = await request({
        url: `/api/business/orders/${id}/payment-status`,
        method: "PATCH",
        data: { paymentStatus },
        headers: { "X-Portal": "business" },
      });
      const payload = res?.data || res || {};

      setOrders((cur) =>
        cur.map((o) =>
          String(o.id) === String(id)
            ? (() => {
                const nextReturnRequest = getNextReturnRequest(o, {
                  status: payload.status || o.status,
                  paymentStatus: payload.paymentStatus || paymentStatus,
                  returnRequestStatus: payload.returnRequestStatus || null,
                });

                return {
                  ...o,
                  status: normalizeOrderStatusLabel(payload.status || o.status),
                  storedStatus: payload.storedStatus || o.storedStatus || null,
                  orderStatus: payload.orderStatus || o.orderStatus,
                  paymentStatus: normalizePaymentStatusLabel(payload.paymentStatus || paymentStatus),
                  paidAt: payload.paidAt ?? o.paidAt,
                  returnRequest: nextReturnRequest,
                  return_request: nextReturnRequest,
                };
              })()
            : o
        )
      );

      if (String(openId) === String(id)) {
        await refreshOrderActivity(id);
      }

      showToast("success", "Payment status updated");
    } catch (e) {
      setOrders(prev);
      const msg = e?.message || "Failed to update payment status.";

      const code = statusFromErrMessage(msg);
      if (code === 403) {
        await Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Access denied",
          html: "You don’t have permission to update payment status.",
          confirmButtonText: "OK",
        });
      } else {
        await Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Update failed",
          html: msg,
          confirmButtonText: "OK",
        });
      }
    }
  }

  const scopedOrders = useMemo(() => orders.filter(order => allowedStatuses.includes(normalizeOrderStatusLabel(order.status))), [orders, allowedStatuses]);
  const filtered = useMemo(() => {
    let list = scopedOrders;

    if (statusFilter !== "All") {
      list = list.filter((o) => normalizeOrderStatusLabel(o.status) === statusFilter);
    }

    if (!qDebounced) return list;

    return list.filter((o) => {
      const blob = `${o.reference || ""} ${o.id || ""} ${o.customer || ""} ${o.email || ""} ${o.status || ""} ${o.paymentStatus || ""} ${o.paymentStatusLabel || ""} ${o.paymentStatusMessage || ""} ${o.customerNote || ""} ${o.returnRequest?.reason || ""} ${o.returnRequest?.details || ""}`.toLowerCase();
      return blob.includes(qDebounced);
    });
  }, [scopedOrders, qDebounced, statusFilter]);

  const counts = useMemo(() => {
    const c = { All: scopedOrders.length };
    for (const s of ORDER_STATUSES) c[s] = 0;
    for (const o of scopedOrders) {
      const s = normalizeOrderStatusLabel(o.status || "Pending");
      if (c[s] != null) c[s] += 1;
    }
    return c;
  }, [scopedOrders]);

  return (
    <div className="space-y-4" id="business-swal-root">
      {pageTitle && <PageHeader title={pageTitle} description={pageSubtitle} />}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[60]">
          <div
            className={
              "rounded-2xl border px-4 py-3 text-sm shadow-lg backdrop-blur " +
              "bg-white/95 text-zinc-900 border-zinc-200 " +
              "dark:bg-[#121214]/95 dark:text-zinc-100 dark:border-zinc-800 " +
              (toast.type === "success" ? " ring-1 ring-emerald-200 dark:ring-emerald-900/40" : "") +
              (toast.type === "info" ? " ring-1 ring-blue-200 dark:ring-blue-900/40" : "") +
              (toast.type === "error" ? " ring-1 ring-red-200 dark:ring-red-900/40" : "")
            }
          >
            {toast.msg}
          </div>
        </div>
      )}

      <Panel
        eyebrow="Orders"
        title={panelTitle}
        right={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search: order ID #, customer, email…"
              className={`w-full sm:w-80 ${FIELD_CLASS_NAME} placeholder:text-zinc-400 dark:placeholder:text-zinc-500`}
            />
          </div>
        }
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-zinc-600 dark:text-zinc-400">Order Status</span>
            <ShopSelect
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={[
                { value: "All", label: `All (${counts.All || 0})` },
                ...allowedStatuses.map((status) => ({ value: status, label: `${status} (${counts[status] || 0})` })),
              ]}
              ariaLabel="Filter orders by status"
              align="start"
              minPanelWidth={192}
              triggerClassName="min-h-10 w-48 rounded-lg px-3 py-2 text-sm"
              panelClassName="py-1"
            />

            <div className="text-xs text-zinc-500 dark:text-zinc-400">
              Showing{" "}
              <span className="font-semibold text-zinc-900 dark:text-zinc-100">{filtered.length}</span> of{" "}
              <span className="font-semibold text-zinc-900 dark:text-zinc-100">{scopedOrders.length}</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-zinc-600 dark:text-zinc-400">Loading orders…</div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-4 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-400">
            No orders found. Try a different search or filter.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-zinc-200 dark:border-zinc-800">
            <table className="w-full min-w-[1360px] text-left border-collapse bg-white dark:bg-[#121214]">
              <thead className="bg-zinc-50 dark:bg-zinc-900/50">
                <tr className="border-b border-zinc-200 dark:border-zinc-800">
                  <th className="p-3 text-xs font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Order ID #</th>
                  <th className="p-3 text-xs font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Customer</th>
                  <th className="p-3 text-xs font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Total</th>
                  <th className="p-3 text-xs font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Payment</th>
                  <th className="p-3 text-xs font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Order Status</th>
                  <th className="p-3 text-xs font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Shipment</th>
                  <th className="p-3 text-xs font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Date</th>
                  <th className="p-3 text-xs font-semibold text-zinc-600 dark:text-zinc-300 whitespace-nowrap">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {filtered.map((o) => {
                  const shopperReviewCount = Array.isArray(o.items)
                    ? o.items.filter((item) => item?.shopperReview || item?.shopper_review).length
                    : 0;

                  return (
                    <tr
                      key={o.id}
                      className={TABLE_ROW_CLASS_NAME}
                      onClick={() => setOpenId(o.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && setOpenId(o.id)}
                    >
                      <td className="p-3 text-sm text-zinc-900 dark:text-zinc-100 whitespace-nowrap">
                        <div className="max-w-[220px] truncate font-semibold" title={getVisibleOrderId(o)}>
                          {getVisibleOrderId(o)}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">Internal #{o.id}</div>
                      </td>

                      <td className="p-3 text-sm text-zinc-700 dark:text-zinc-200">
                        <div className="font-medium">{o.customer || "-"}</div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{o.email || ""}</div>
                      </td>

                      <td className="p-3 text-sm text-zinc-700 dark:text-zinc-200 whitespace-nowrap">{money(o.total)}</td>

                      <td className="p-3 text-sm">
                        <div className="space-y-1">
                          <span className={paymentStatusChipClasses(o.paymentStatusLabel || o.paymentStatus)}>{o.paymentStatusLabel || o.paymentStatus || "Pending"}</span>
                          <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
                            {o.paymentMethod || "Method pending"}
                          </div>
                          {o.paymentStatusMessage ? (
                            <div className="max-w-[280px] text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                              {o.paymentStatusMessage}
                            </div>
                          ) : null}
                        </div>
                      </td>

                      <td className="p-3 text-sm">
                        <div className="space-y-1">
                          <span className={orderStatusChipClasses(o.status)}>{o.status || "Pending"}</span>
                          {o.returnRequest ? (
                            <span className={returnRequestChipClasses(o.returnRequest.status)}>
                              Return Request {getReturnRequestStatusLabel(o.returnRequest.status)}
                            </span>
                          ) : null}
                          {isShopperCancellationRequested(o) ? (
                            <>
                              <div className="text-[11px] font-medium text-rose-600 dark:text-rose-300">
                                Shopper requested cancellation
                              </div>
                              <div className="text-[11px] text-rose-600/90 dark:text-rose-200/90">
                                Reason: {getShopperCancellationReason(o)}
                              </div>
                            </>
                          ) : null}
                          {o.returnRequest?.reason ? (
                            <div className="max-w-[240px] text-[11px] leading-relaxed text-orange-700 dark:text-orange-200">
                              Return reason: {o.returnRequest.reason}
                            </div>
                          ) : null}
                          {Array.isArray(o.returnRequest?.evidence) && o.returnRequest.evidence.length > 0 ? (
                            <div className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-200">
                              {o.returnRequest.evidence.length} evidence file{o.returnRequest.evidence.length === 1 ? "" : "s"}
                            </div>
                          ) : null}
                          {o.returnRequest?.refundAmount != null && Number(o.returnRequest.refundAmount) > 0 ? (
                            <div className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700 dark:border-orange-900/40 dark:bg-orange-950/30 dark:text-orange-200">
                              Refund {money(o.returnRequest.refundAmount)}
                            </div>
                          ) : null}
                          {String(o.returnRequest?.refundDestination || "").trim().toUpperCase() === "WALLET" ? (
                            <div className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
                              Shopper wallet
                            </div>
                          ) : null}
                          {shopperReviewCount > 0 ? (
                            <div className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
                              {shopperReviewCount} shopper review{shopperReviewCount === 1 ? "" : "s"}
                            </div>
                          ) : null}
                          {o.customerNote ? (
                            <div className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700 dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-200">
                              Shopper message attached
                            </div>
                          ) : null}
                        </div>
                      </td>

                      <td className="p-3 text-sm text-zinc-700 dark:text-zinc-200">
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">See Delivery for parcel progress</span>
                      </td>

                      <td className="p-3 text-sm text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                        {safeDateTime(o.createdAt)}
                      </td>

                      <td className="p-3 text-sm">
                        <div className="grid min-w-[320px] gap-2" onClick={(e) => e.stopPropagation()}>
                          <div className="grid grid-cols-[88px_minmax(0,1fr)] items-center gap-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                              Payment
                            </span>
                            {o.canEditPaymentStatus ? (
                              <ShopSelect
                                value={o.paymentStatus || "Pending"}
                                onValueChange={(value) => changePaymentStatus(o.id, value)}
                                options={getPaymentStatusOptions(o.paymentStatus).map((status) => ({
                                  value: status,
                                  label: status,
                                }))}
                                ariaLabel={`Payment status for order ${getVisibleOrderId(o)}`}
                                align="end"
                                minPanelWidth={200}
                                triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
                                panelClassName="py-1"
                              />
                            ) : (
                              <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-medium text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300">
                                {getBusinessPaymentSystemNote(o)}
                              </div>
                            )}
                          </div>

                          <OrderFulfillmentActions order={o} onChange={changeOrderStatus} />

                          {o.fulfillmentNotice ? (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                              {o.fulfillmentNotice}
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <div className={"fixed inset-0 z-[55] " + (openOrder ? "pointer-events-auto" : "pointer-events-none")} aria-hidden={!openOrder}>
        <div
          className={"absolute inset-0 bg-black/40 transition " + (openOrder ? "opacity-100" : "opacity-0")}
          onClick={() => setOpenId(null)}
        />

        <div
          className={
            "absolute right-0 top-0 h-full w-full sm:w-[460px] bg-white dark:bg-[#0b0b0c] " +
            "border-l border-zinc-200 dark:border-zinc-800 shadow-2xl transition-transform " +
            (openOrder ? "translate-x-0" : "translate-x-full")
          }
        >
          <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
            <div>
              <div className="text-sm text-zinc-500 dark:text-zinc-400">Order ID #</div>
              <div className="text-lg font-semibold break-all text-zinc-900 dark:text-zinc-100">{getVisibleOrderId(openOrder)}</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">Internal #{openOrder?.id || "-"}</div>
            </div>

            <button
              type="button"
              onClick={() => setOpenId(null)}
              className={SECONDARY_BUTTON_CLASS_NAME}
            >
              Close
            </button>
          </div>

          <div className="p-4 space-y-4 overflow-y-auto h-[calc(100%-72px)]">
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">Order Status</div>
                  <div className="mt-1">
                    <span className={orderStatusChipClasses(openOrder?.status)}>{openOrder?.status || "Pending"}</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">Payment Status</div>
                  <div className="mt-1">
                    <span className={paymentStatusChipClasses(openOrder?.paymentStatusLabel || openOrder?.paymentStatus)}>{openOrder?.paymentStatusLabel || openOrder?.paymentStatus || "Pending"}</span>
                  </div>
                </div>
              </div>

              {openOrder?.paymentStatusMessage ? (
                <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs leading-relaxed text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300">
                  {openOrder.paymentStatusMessage}
                </div>
              ) : null}

              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">Placed</div>
                  <div className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {safeDateTime(openOrder?.createdAt)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">Payment Method</div>
                  <div className="mt-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {openOrder?.paymentMethod || "-"}
                  </div>
                </div>
              </div>

              {isShopperCancellationRequested(openOrder) ? (
                <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
                  <div className="font-semibold">Shopper requested cancellation.</div>
                  <div className="mt-1 text-rose-700/90 dark:text-rose-100/90">
                    Reason: {getShopperCancellationReason(openOrder)}
                  </div>
                </div>
              ) : null}

              <div className="mt-3 grid grid-cols-1 gap-3">
                <div>
                  <label className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">Payment Status</label>
                  {openOrder?.canEditPaymentStatus ? (
                    <ShopSelect
                      value={openOrder?.paymentStatus || "Pending"}
                      onValueChange={(value) => changePaymentStatus(openOrder.id, value)}
                      options={getPaymentStatusOptions(openOrder?.paymentStatus).map((status) => ({ value: status, label: status }))}
                      ariaLabel="Payment Status"
                      align="start"
                      minPanelWidth={200}
                      triggerClassName="mt-1 min-h-10 w-full rounded-lg px-3 py-2 text-sm"
                      panelClassName="py-1"
                    />
                  ) : (
                    <div className="mt-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-medium text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300">
                      {getBusinessPaymentSystemNote(openOrder)}
                    </div>
                  )}
                </div>

                <OrderFulfillmentActions order={openOrder} onChange={changeOrderStatus} />

                <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
                  {openOrder?.fulfillmentNotice || "Payment status updates the payment record. Order status updates fulfillment only. Use the dedicated Returns & Refunds workflow to reject requests or credit shopper wallet safely."}
                </div>
              </div>
            </div>

            {openOrder?.customerNote ? (
              <div className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4 dark:border-sky-900/40 dark:bg-sky-950/20">
                <div className="flex items-center gap-2 text-sm font-semibold text-sky-900 dark:text-sky-100">
                  <MessageSquare size={16} className="text-sky-600 dark:text-sky-300" />
                  Checkout Message
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-sky-800 dark:text-sky-100/90">
                  {openOrder.customerNote}
                </p>
              </div>
            ) : null}

            <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
              <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Shipping Details</div>
              <div className="mt-2 text-sm text-zinc-700 dark:text-zinc-200">
                {openOrder?.shippingAddress ? (
                  <div>
                    <div className="font-medium">Address</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">{openOrder.shippingAddress}</div>
                  </div>
                ) : <div className="text-xs text-zinc-500">No shipping address recorded.</div>}

                {openOrder?.billingAddress && openOrder.billingAddress !== openOrder.shippingAddress && (
                  <div className="mt-2">
                    <div className="font-medium">Billing Address</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">{openOrder.billingAddress}</div>
                  </div>
                )}
              </div>

              <Link to="/business/delivery?view=parcels" className="mt-4 inline-flex min-h-11 items-center font-semibold text-emerald-700 underline dark:text-emerald-300">Manage parcel, rider and pickup in Delivery</Link>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
              <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Items</div>
              {Array.isArray(openOrder?.items) && openOrder.items.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {openOrder.items.map((it, idx) => {
                    const name = it?.name || it?.productName || "Item";
                    const variation = it?.variation || "";
                    const sku = it?.sku || "";
                    const qty = it?.quantity ?? 1;
                    const price = it?.price ?? null;
                    const shopperReview = it?.shopperReview || it?.shopper_review || null;

                    return (
                      <div key={`${idx}-${sku}-${name}`} className="rounded-xl border border-zinc-200 p-3 dark:border-zinc-800">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{name}</div>
                            {variation ? (
                              <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                                Variation: {variation}
                              </div>
                            ) : null}
                            {sku ? (
                              <div className="mt-1 font-mono text-[11px] text-zinc-500 dark:text-zinc-400">
                                SKU: {sku}
                              </div>
                            ) : null}
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-zinc-500 dark:text-zinc-400">Qty</div>
                            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{qty}</div>
                            {price != null ? <div className="text-xs text-zinc-500 dark:text-zinc-400">{money(price)}</div> : null}
                          </div>
                        </div>

                        {shopperReview ? (
                          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="font-semibold">Shopper Review</div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full border border-emerald-300 bg-white px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                                  Rating {shopperReview.rating || 0}/5
                                </span>
                                {shopperReview.status ? (
                                  <span className="text-[11px] text-emerald-700/90 dark:text-emerald-100/90">
                                    {String(shopperReview.status).toUpperCase()}
                                  </span>
                                ) : null}
                                {shopperReview.recommended != null ? (
                                  <span className="text-[11px] text-emerald-700/90 dark:text-emerald-100/90">
                                    {shopperReview.recommended ? "Recommended" : "Not recommended"}
                                  </span>
                                ) : null}
                              </div>
                            </div>

                            {shopperReview.title ? (
                              <div className="mt-2 font-medium text-emerald-900 dark:text-emerald-100">
                                {shopperReview.title}
                              </div>
                            ) : null}

                            {shopperReview.reviewText ? (
                              <div className="mt-1 whitespace-pre-wrap text-emerald-800/90 dark:text-emerald-100/90">
                                {shopperReview.reviewText}
                              </div>
                            ) : null}

                            <ReviewMediaGallery
                              media={shopperReview.media}
                              className="mt-3"
                            />

                            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-medium text-emerald-700/90 dark:text-emerald-100/80">
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100/90">
                                <Heart size={11} className="text-rose-500 dark:text-rose-300" />
                                {shopperReview.likeCount || 0} likes
                              </span>
                              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-2.5 py-1 dark:border-emerald-800 dark:bg-emerald-950/40">
                                Submitted {safeDateTime(shopperReview.createdAt)}
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">No items data.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BusinessOrders(props) {
  return props.pageTitle ? <LegacyBusinessOrders {...props} /> : <BusinessOrdersIndex />;
}
